import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';
import Promise from 'bluebird';
import PropTypes from 'prop-types';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { WithAudienceAssociationsRepository, WithIMARepository } from './repository';
import { StyledIMAReachModal } from './ima-reach-modal';
import { CountryCodeToDistrictionChannelMap, listSizeConst } from './consts';
import { getIMAAssocaitonInfo } from './ima-reach-warning';

let hideModal = true;
const calculateAudienceReachSize = ({ audience, locationsInfo }) => {
  let reach = 0;
  let aimReach = 0;
  const {
    locationIntentCriterion,
    targetedLocationCriterions,
    excludedLocationCriterions,
  } = locationsInfo;

  _.each(audience.InternalizationReach.Values, (internalizationReachChannel, channelIndex) => {
    if (locationIntentCriterion && locationIntentCriterion.indexOf('PeopleIn') !== -1) {
      let supportedMarkets = internalizationReachChannel.Keys;

      if (targetedLocationCriterions && targetedLocationCriterions.length > 0) {
        // there are target locations
        const targetedMarkets = _.chain(targetedLocationCriterions)
          .filter(countryCode => _.has(CountryCodeToDistrictionChannelMap, countryCode))
          .map(countryCode => CountryCodeToDistrictionChannelMap[countryCode])
          .flatten()
          .value();

        supportedMarkets = _.intersection(internalizationReachChannel.Keys, targetedMarkets);
      } else if (excludedLocationCriterions && excludedLocationCriterions.length > 0) {
        // there is no target location, but there are excluded locations
        const excludedMarkets = _.chain(excludedLocationCriterions)
          .filter(countryCode => _.has(CountryCodeToDistrictionChannelMap, countryCode))
          .map(countryCode => CountryCodeToDistrictionChannelMap[countryCode])
          .flatten()
          .value();

        supportedMarkets = _.difference(internalizationReachChannel.Keys, excludedMarkets);
      }

      const supportedMarketsValues = [];

      _.each(internalizationReachChannel.Keys, (internalizationReachChannelKey, index) => {
        if (_.contains(supportedMarkets, internalizationReachChannelKey) &&
          // ensure each channel reach size eligible
          internalizationReachChannel.Values[index] > listSizeConst.minimumEligibleAudienceSize) {
          supportedMarketsValues.push(internalizationReachChannel.Values[index]);

          if (audience.InternalizationReach.Keys[channelIndex] === 'AIM') {
            aimReach += internalizationReachChannel.Values[index];
          } else {
            reach += internalizationReachChannel.Values[index];
          }
        }
      });

      // rewrite the total reach size.
      if (audience.InternalizationReach.Keys[channelIndex] === 'AIM') {
        _.extend(audience, { IMA_Calculated_AIMReach: aimReach });
      } else {
        _.extend(audience, { IMA_Calculated_Reach: reach });
      }
    } else if (audience.InternalizationReach.Keys[channelIndex] === 'AIM') {
      _.extend(audience, { IMA_Calculated_AIMReach: audience.AIMReach });
    } else {
      _.extend(audience, { IMA_Calculated_Reach: audience.Reach });
    }
  });
};

const calculateAudienceEligible = ({ audience, locationsInfo, deliveryChannel }) => {
  calculateAudienceReachSize({ audience, locationsInfo });

  const reachSize = deliveryChannel === 'AIM' ? audience.IMA_Calculated_AIMReach : audience.IMA_Calculated_Reach;
  return reachSize > 0;
};

const isAudienceEligible = ({
  audienceAssociation,
  audiences,
  locationsInfo,
  deliveryChannel,
}) => {
  const audience = _.chain(audiences)
    .result('value', [])
    .findWhere({ Id: audienceAssociation.AudienceCriterion.AudienceId })
    .value();

  return !audience || calculateAudienceEligible({
    audience,
    locationsInfo,
    deliveryChannel,
  });
};

const IMAReachModalShowWithConditions = ({
  audiences,
  audienceAssociations,
  locationsInfo,
  onYes,
  onCancel,
  deliveryChannel,
  i18n,
  resolveRenderResult,
}) => {
  const [open, setOpen] = React.useState(true);

  if (audiences.dataNotReady) {
    return null;
  }
  const closeModal = () => setOpen(false);
  const onYesClick = () => {
    closeModal();
    onYes();
  };
  const onCancelClick = () => {
    closeModal();
    onCancel();
  };
  const inEligibleAudiences = _.chain(audienceAssociations)
    .filter(audienceAssociation => !isAudienceEligible({
      audienceAssociation,
      audiences,
      locationsInfo,
      deliveryChannel,
    }))
    .map(audienceAssociation => audienceAssociation.AudienceCriterion.AudienceName)
    .value();

  if (inEligibleAudiences && inEligibleAudiences.length > 0) {
    if (!hideModal) {
      setOpen(true);
    }
    resolveRenderResult(true);

    return (<StyledIMAReachModal
      onYes={onYesClick}
      onCancel={onCancelClick}
      ineligibleAudiences={inEligibleAudiences}
      hideDialog={!open}
      i18n={i18n}
    />);
  }
  resolveRenderResult(false);

  return null;
};

IMAReachModalShowWithConditions.propTypes = {
  audiences: PropTypes.shape({}).isRequired,
  audienceAssociations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locationsInfo: PropTypes.shape({}).isRequired,
  onYes: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  deliveryChannel: PropTypes.string.isRequired,
  resolveRenderResult: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

const IMAReachModalComponentWithAudienceRepository = props => (
  <WithIMARepository
    WrappedComponent={IMAReachModalShowWithConditions}
    {...props}
  />);

const IMAReachModalComponentShowWithConditions = (props) => {
  const { dataNotReady } = props.audienceAssociations;
  if (dataNotReady) {
    return null;
  }

  const {
    audienceAssociations,
    adGroupId,
    resolveRenderResult,
  } = props;
  const {
    isIMAReachWarningShow,
    adGroupAudienceAssociations,
    campaignAudienceAssociations,
  } = getIMAAssocaitonInfo({ audienceAssociations, adGroupId });

  if (isIMAReachWarningShow) {
    const associations = adGroupId ? adGroupAudienceAssociations : campaignAudienceAssociations;
    return (<IMAReachModalComponentWithAudienceRepository
      {...props}
      audienceAssociations={associations}
    />);
  }
  resolveRenderResult(false);

  return null;
};

IMAReachModalComponentShowWithConditions.propTypes = {
  adGroupId: PropTypes.number,
  audienceAssociations: PropTypes.shape({}).isRequired,
  resolveRenderResult: PropTypes.func.isRequired,
};

IMAReachModalComponentShowWithConditions.defaultProps = {
  adGroupId: null,
};

const IMAReachModalComponent = props => (
  <WithAudienceAssociationsRepository
    WrappedComponent={IMAReachModalComponentShowWithConditions}
    {...props}
  />);

export const showIMAReachModal = ({
  cid,
  aid,
  campaignId,
  locationsInfo,
  onYes,
  onCancel = () => {},
  deliveryChannel,
  i18n,
  adGroupId = null,
  audienceAssociations = null, // for creation workflow, audience assocaition is from outside.
}) => {
  let dom = document.querySelector('body > .ima-reach-modal-container');
  if (!dom) {
    const body = document.querySelector('body');
    dom = document.createElement('div');

    dom.className = 'ima-reach-modal-container';
    body.appendChild(dom);
  }
  hideModal = false;

  return new Promise((resolve) => {
    const resolveRenderResult = (isIMAReachModalShow) => {
      hideModal = true;
      resolve(isIMAReachModalShow);
    };
    ReactDOM.render(
      <ScopeContext.Provider value={{
        scope: {
          values: {
            cid, aid, adGroupId, campaignId,
            },
          },
      }}
      >
        {audienceAssociations ?
        (<IMAReachModalComponentShowWithConditions
          audienceAssociations={audienceAssociations}
          adGroupId={adGroupId}
          locationsInfo={locationsInfo}
          onYes={onYes}
          onCancel={onCancel}
          i18n={i18n}
          deliveryChannel={deliveryChannel}
          resolveRenderResult={resolveRenderResult}
        />) :
        (<IMAReachModalComponent
          campaignId={campaignId}
          adGroupId={adGroupId}
          cid={cid}
          aid={aid}
          locationsInfo={locationsInfo}
          onYes={onYes}
          onCancel={onCancel}
          i18n={i18n}
          deliveryChannel={deliveryChannel}
          resolveRenderResult={resolveRenderResult}
        />)
        }
      </ScopeContext.Provider>,
      dom
    );
  });
};
